import { useEffect, useRef } from 'react';
import { type MediaHandlerProps, useMediaUtils } from '..';

type YT_Type = {
  Player: (iframe: string | HTMLIFrameElement, options?: Player_Options) => void;
  PlayerState: PlayerState;
};

type Player = {
  // getters
  getDuration: () => number;
  getCurrentTime: () => number;
  getVideoData: () => unknown;
  getVolume: () => number;

  // info
  isMuted: () => boolean;
  videoTitle: () => string;

  // actions
  destroy: () => void;
  mute: () => void;
  unMute: () => void;
  nextVideo: () => void;
  previousVideo: () => void;
  playVideo: () => void;
  playVideoAt: (time: number) => void;
  pauseVideo: () => void;
  seekTo: (time: number, allowSeekAhead: boolean) => void;
  setVolume: (volume: number) => void;
  stopVideo: () => void;
};

type PlayerState = {
  UNSTARTED: -1;
  ENDED: 0;
  PLAYING: 1;
  PAUSED: 2;
  BUFFERING: 3;
  CUED: 5;
};

type Player_Options = {
  height?: string;
  width?: string;
  videoId?: string;
  playerVars?: Record<string, string | number | boolean>;
  events?: {
    onReady?: (event: unknown) => void;
    onStateChange?: (event: unknown) => void;
    onError?: (event: unknown) => void;
    onPlaybackQualityChange?: (event: unknown) => void;
    onPlaybackRateChange?: (event: unknown) => void;
    onApiChange?: (event: unknown) => void;
  };
};

type PlaybackEvent = {
  currentTime: number;
};

type EventData = {
  data: number;
  target?: Player;
};

export const useYoutubeHandler = ({ eventName }: MediaHandlerProps) => {
  const YT = useRef<YT_Type | null>(null);
  const player = useRef<Player | null>(null);

  const { isFirstPlay, push, getIframe, getPercentage, playbackDuration, loadScript, checkSrcType } =
    useMediaUtils(eventName);

  const initPlayer = () => {
    if (!YT.current || !YT?.current?.Player) return;
    if (!!player.current) return;

    const iframe = getIframe();
    if (!iframe) return;

    if (!YT?.current?.Player) return;
    // @ts-ignore
    player.current = new YT.current.Player(iframe, {
      events: {
        onReady: onReady,
        onStateChange: onStateChange,
      },
    }) as Player;
  };

  const onReady = (event: EventData | unknown) => {
    if (playbackDuration.current === 0 && (event as EventData)?.target)
      playbackDuration.current = (event as EventData)?.target?.getDuration() as number;
  };

  const onStateChange = (_event: EventData | unknown) => {
    const event = _event as EventData;
    // set the duration if it is not set yet
    if (playbackDuration.current === 0 || ((event as EventData)?.target?.getDuration() ?? 0) > playbackDuration.current)
      playbackDuration.current = (event as EventData)?.target?.getDuration() ?? 0;

    if (event.data === YT?.current?.PlayerState.PLAYING && !isFirstPlay.current) {
      // if the state change is a playing event, we know this is a play event
      isFirstPlay.current = true;
      push('play');
    }
  };

  const playbackEvent = (data: PlaybackEvent) => {
    //  duration minus 1 second to account for the last second not being tracked in youtube
    const duration = playbackDuration.current - 1;

    const percentage = getPercentage(data?.currentTime, duration);
    if (!!percentage) push(percentage);
  };

  useEffect(() => {
    const initYoutube = () => {
      YT.current = window.YT;
      initPlayer();
    };
    // set youtube api init function
    window.onYouTubeIframeAPIReady = initYoutube;

    // script need to be loaded as early as possible
    loadScript('https://www.youtube.com/iframe_api', initYoutube);

    // playback event is only transmitted through postmessage api
    // YoutubeApi needs to be loaded in order for this message to pass through
    const listener = (event: MessageEvent) => {
      if (checkSrcType(event.origin) !== 'youtube') return;
      if (!YT.current) return;
      if (!player.current) return;

      try {
        const data = JSON.parse(event.data);

        // only send playback events when the data is a "infoDelivery" event
        // data.info exists AND data info has as currentTime property
        if (data?.event === 'infoDelivery' && !!data?.info && !!data?.info?.currentTime) {
          playbackEvent(data.info);
        }
      } catch (e) {
        return;
      }
    };

    window.addEventListener('message', listener);

    return () => {
      window.onYouTubeIframeAPIReady = undefined;
      window.removeEventListener('message', listener);

      if (!!player.current) {
        player?.current?.destroy();
        player.current = null;
      }
    };
  }, []);
};
